import React, { useMemo } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { saheehTheme } from '../theme';
import { useLang } from './LanguageProvider';

/**
 * Wraps the app with a direction-aware MUI theme + emotion style cache so
 * every component flips for Arabic without us touching individual `sx` props.
 *
 * Why two caches? Emotion has to inject CSS that knows about the page direction
 * at build-time; we can't mutate one cache between RTL and LTR. So we keep two
 * caches in memory and switch which one is active when the language changes.
 */
export const DirectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang, dir } = useLang();

  const cache = useMemo(
    () =>
      createCache({
        key: dir === 'rtl' ? 'mui-rtl' : 'mui',
        stylisPlugins: dir === 'rtl' ? [prefixer, rtlPlugin] : [prefixer],
      }),
    [dir],
  );

  // createTheme(base, overrides) properly extends the base theme — spreading
  // it into a fresh createTheme call loses computed values (typography
  // pixel sizes, palette tokens). The two-arg form is the documented way
  // to override one field (direction).
  const theme = useMemo(
    () => createTheme(saheehTheme, { direction: dir }),
    [dir],
  );

  // key={lang} forces the entire subtree to remount when the user toggles
  // languages — without it MUI components keep stale margin/padding values
  // computed against the previous direction.
  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <div key={lang} dir={dir} style={{ display: 'contents' }}>
          {children}
        </div>
      </ThemeProvider>
    </CacheProvider>
  );
};
